import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Loader2, Send, Video } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const empty = { name: "", email: "", phone: "", message: "", slot: "" };

export default function Contact() {
  const { content } = useSite();
  const [contact, setContact] = useState(empty);
  const [resv, setResv] = useState(empty);
  const [l1, setL1] = useState(false);
  const [l2, setL2] = useState(false);

  const wa = content ? `https://wa.me/${content.whatsapp}` : "#";

  const submitContact = async (e) => {
    e.preventDefault();
    setL1(true);
    try {
      const { data } = await api.post("/contact", { ...contact, type: "contact" });
      toast.success(data.message);
      setContact(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setL1(false);
    }
  };

  const submitResv = async (e) => {
    e.preventDefault();
    setL2(true);
    try {
      const { data } = await api.post("/contact", { ...resv, type: "reservation", message: resv.message || "Demande de consultation" });
      toast.success(data.message);
      setResv(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setL2(false);
    }
  };

  const field =
    "w-full px-4 py-3 rounded-xl bg-black/40 border border-amber-500/20 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60";

  return (
    <div data-testid="contact-page" className="pt-32 pb-16 px-5 max-w-6xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-xs tracking-[0.28em] uppercase font-mono-accent text-amber-400/70 mb-4">Contact</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-amber-50 mb-5">Entrons en relation</h1>
        <p className="text-slate-400 text-lg">Une question, un projet, une consultation ? Nous sommes là.</p>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <motion.form
          onSubmit={submitContact}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass rounded-2xl p-8"
          data-testid="contact-form"
        >
          <h2 className="font-serif text-2xl text-amber-100 mb-6">Formulaire de contact</h2>
          <div className="flex flex-col gap-4">
            <input required placeholder="Nom" data-testid="contact-form-name-input" className={field} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
            <input required type="email" placeholder="Email" data-testid="contact-form-email-input" className={field} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
            <input placeholder="Téléphone (optionnel)" data-testid="contact-form-phone-input" className={field} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
            <textarea required rows={4} placeholder="Votre message" data-testid="contact-form-message-input" className={field} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
            <button type="submit" disabled={l1} data-testid="contact-form-submit-button" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.02] transition-transform disabled:opacity-60">
              {l1 ? <Loader2 size={18} className="animate-spin" /> : <><Send size={16} /> Envoyer</>}
            </button>
          </div>
        </motion.form>

        <motion.form
          onSubmit={submitResv}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="glass rounded-2xl p-8"
          data-testid="reservation-form"
        >
          <h2 className="font-serif text-2xl text-amber-100 mb-2">Demande de consultation</h2>
          <p className="text-slate-400 text-sm mb-2">Réservez un échange individuel. Indiquez votre créneau souhaité.</p>
          <p className="text-amber-300/80 text-xs font-mono-accent mb-6 inline-flex items-center gap-2">
            <Video size={14} /> Conférences &amp; sessions via WhatsApp et Zoom
          </p>
          <div className="flex flex-col gap-4">
            <input required placeholder="Nom" data-testid="resv-name-input" className={field} value={resv.name} onChange={(e) => setResv({ ...resv, name: e.target.value })} />
            <input required type="email" placeholder="Email" data-testid="resv-email-input" className={field} value={resv.email} onChange={(e) => setResv({ ...resv, email: e.target.value })} />
            <input placeholder="Créneau souhaité (ex: mardi après-midi)" data-testid="resv-slot-input" className={field} value={resv.slot} onChange={(e) => setResv({ ...resv, slot: e.target.value })} />
            <textarea rows={3} placeholder="Un mot sur votre demande (optionnel)" data-testid="resv-message-input" className={field} value={resv.message} onChange={(e) => setResv({ ...resv, message: e.target.value })} />
            <button type="submit" disabled={l2} data-testid="resv-submit-button" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-amber-500/40 text-amber-100 hover:bg-amber-500/10 font-medium transition-colors disabled:opacity-60">
              {l2 ? <Loader2 size={18} className="animate-spin" /> : "Réserver une consultation"}
            </button>
          </div>
          <div className="mt-6 pt-6 border-t border-amber-500/15 text-center">
            <p className="text-slate-400 text-sm mb-3">Ou écrivez-nous directement sur WhatsApp</p>
            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-cta-button" className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200">
              <MessageCircle size={18} /> Parler sur WhatsApp
            </a>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
